<template>
  <HeaderC />

  <main id="main" class="relative overflow-x-clip bg-white dark:bg-slate-900 transition-colors duration-300">
    
    <section v-if="enabled.hero" id="hero" aria-label="Hero" class="py-12 sm:py-16 lg:py-24">
      <HeroComponent />
    </section>

    <section v-if="enabled.services" id="services" aria-label="Nos services" class="py-12 sm:py-16 lg:py-24">
      <div class="relative w-[100dvw] left-1/2 -translate-x-1/2 overflow-x-clip bg-white dark:bg-slate-900 transition-colors duration-300">
        <div class="max-w-[92rem] mx-auto px-4 sm:px-6 lg:px-8">
          <ServicesComponent :popular-only="true" :limit="8" />
        </div>
      </div>
    </section>

    <section v-if="enabled.projects" id="projects" aria-label="Nos réalisations" class="py-12 sm:py-16 lg:py-24" ref="projectsEl">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Suspense>
          <template #default>
            <component v-if="projectsVisible" :is="RealisationAsync" />
          </template>
          <template #fallback>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" role="status" aria-live="polite">
              <div v-for="i in 6" :key="'skp-' + i"
                   class="h-44 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 animate-pulse" />
            </div>
          </template>
        </Suspense>
      </div>
    </section>

    <section v-if="enabled.testimonials" id="testimonials" aria-label="Témoignages" class="py-12 sm:py-16 lg:py-24" ref="testimonialsEl">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Suspense>
          <template #default>
            <component v-if="testimonialsVisible" :is="TemoignageAsync" />
          </template>
          <template #fallback>
            <div class="space-y-3 py-2" role="status" aria-live="polite">
              <div class="h-3 rounded bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 animate-pulse w-11/12"></div>
              <div class="h-3 rounded bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 animate-pulse w-10/12"></div>
              <div class="h-3 rounded bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 animate-pulse w-9/12"></div>
            </div>
          </template>
        </Suspense>
      </div>
    </section>
  </main>

  <FooterC />

  <button
    v-show="showTop"
    @click="scrollTop"
    class="fixed bottom-6 right-6 z-40 grid place-items-center h-10 w-10 rounded-xl
           text-white bg-gradient-to-br from-brand-500 to-indigo-600 shadow-lg
           hover:brightness-110 active:scale-95 transition
           dark:ring-1 dark:ring-white/20"
    aria-label="Remonter en haut">
    <i class="fa-solid fa-arrow-up"></i>
  </button>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, defineAsyncComponent, reactive, computed, watch } from "vue";
import { useStore } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

/* Above-the-fold */
import HeaderC from "../../components/Visitor_Component/Shared/HighHeader.vue";
import FooterC from "../../components/Visitor_Component/Shared/FooterComponent.vue";
import HeroComponent from "../../components/Visitor_Component/Home_Component/HeroComponent.vue";
import ServicesComponent from "../../components/Visitor_Component/Home_Component/ServicesComponent.vue";

/* Lazy loaded */
const RealisationAsync = defineAsyncComponent(() =>
  import(/* webpackChunkName: "home-realisation" */ "../../components/Visitor_Component/Home_Component/RealisationComponent.vue")
);
const TemoignageAsync = defineAsyncComponent(() =>
  import(/* webpackChunkName: "home-temoignage" */ "../../components/Visitor_Component/Home_Component/TemoignageComponent.vue")
);

export default defineComponent({
  name: "HomeView",
  components: { HeaderC, FooterC, HeroComponent, ServicesComponent },
  setup() {
    const store = useStore();

    // DOM refs observées
    const projectsEl = ref(null);
    const testimonialsEl = ref(null);

    // Visibilité viewport
    const projectsVisible = ref(false);
    const testimonialsVisible = ref(false);

    // Activation depuis la BDD (par défaut ON pour rétro-compat)
    const enabled = reactive({ hero: true, services: true, projects: true, testimonials: true });

    // NOTE : On n'a plus besoin des computed combinés "projectsShow" car on a séparé la logique :
    // - enabled.projects sur la <section>
    // - projectsVisible sur le <component>
    
    const showTop = ref(false);
    const currentLang = computed(() => store.getters.getLangue || "fr");

    let ioProjects = null;
    let ioTestimonials = null;

    function makeObserver(cb) {
      if (!("IntersectionObserver" in window)) {
        cb();
        return { observe() {}, disconnect() {} };
      }
      const prefersReduced =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      return new IntersectionObserver(
        (entries, obs) =>
          entries.forEach((e) => {
            if (e.isIntersecting) {
              cb();
              obs.unobserve(e.target);
            }
          }),
        { root: null, rootMargin: prefersReduced ? "0px" : "120px", threshold: 0.15 }
      );
    }

    const onScroll = () => { showTop.value = window.scrollY > 420; };

    function scrollTop() {
      const prefersReduced =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
    }

    async function fetchEnabledMap() {
      try {
        const out = await fetchPageIdByCode("home", currentLang.value);
        const pageId = typeof out === "number" ? out : (out?.page_id ?? out?.id ?? null);
        if (!pageId) return;

        const { data } = await http.get("page-components", { params: { page_id: pageId } });
        const items = Array.isArray(data?.items) ? data.items : [];

        enabled.hero = enabled.services = enabled.projects = enabled.testimonials = false;
        for (const it of items) {
          const code = String(it.code || "").trim();
          const on = Number(it.is_enabled) === 1 || it.is_enabled === true;
          if (code in enabled) enabled[code] = !!on;
        }
      } catch (e) {
        console.warn("[HomeView] fetchEnabledMap:", e?.message || e);
      }
    }

    function ensureFirstRevealFallback() {
      setTimeout(() => {
        // Fallback de sécurité : si jamais l'IO ne se déclenche pas, on force l'affichage
        // uniquement si la section est activée
        if (!projectsVisible.value && enabled.projects) projectsVisible.value = true;
        if (!testimonialsVisible.value && enabled.testimonials) testimonialsVisible.value = true;
      }, 300);
    }

    onMounted(async () => {
      // IO : On observe seulement si la ref existe (donc si enabled=true au départ)
      // Note : Si fetchEnabledMap désactive une section après coup, Vue retirera le DOM, l'observer ne servira plus, c'est OK.
      if (projectsEl.value && !projectsVisible.value) {
        ioProjects = makeObserver(() => { projectsVisible.value = true; });
        ioProjects.observe(projectsEl.value);
      }
      if (testimonialsEl.value && !testimonialsVisible.value) {
        ioTestimonials = makeObserver(() => { testimonialsVisible.value = true; });
        ioTestimonials.observe(testimonialsEl.value);
      }

      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();

      await fetchEnabledMap();
      ensureFirstRevealFallback();
    });

    watch(currentLang, async () => {
      await fetchEnabledMap();
      ensureFirstRevealFallback();
    });

    onBeforeUnmount(() => {
      ioProjects && ioProjects.disconnect?.();
      ioTestimonials && ioTestimonials.disconnect?.();
      window.removeEventListener("scroll", onScroll);
    });

    return {
      RealisationAsync,
      TemoignageAsync,
      projectsEl,
      testimonialsEl,
      projectsVisible,
      testimonialsVisible,
      enabled,
      showTop,
      scrollTop,
    };
  },
});
</script>

<style scoped>
/* rien d'autre, tout est en utilitaires */
</style>